// ===============================================================
// UYGULAMA SÜRÜM & ZORUNLU GÜNCELLEME SERVİSİ
// ===============================================================
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// BU KOD PAKETİNİN MEVCUT SÜRÜMÜ
export let CURRENT_CLIENT_VERSION = '1.14';

// Cihazın gerçek Android sürümünü oku
if (typeof window !== 'undefined' && Capacitor.isNativePlatform()) {
  CapApp.getInfo().then(info => {
    if (info && info.version) {
      CURRENT_CLIENT_VERSION = info.version;
    }
  }).catch(() => {});
}

const LOCAL_VERSION_KEY = 'kocum_app_version_config';

/**
 * İki semantik sürüm numarasını karşılaştırır (Örn: "1.1.0" ile "1.2.0")
 * Dönüş:
 * -1 : v1 < v2 (v1 eski sürüm)
 *  0 : v1 === v2 (aynı sürüm)
 *  1 : v1 > v2 (v1 daha yeni)
 */
export function compareVersions(v1 = '1.0.0', v2 = '1.0.0') {
  const parts1 = String(v1).replace(/[^0-9.]/g, '').split('.').map(n => parseInt(n, 10) || 0);
  const parts2 = String(v2).replace(/[^0-9.]/g, '').split('.').map(n => parseInt(n, 10) || 0);

  const maxLen = Math.max(parts1.length, parts2.length, 3);
  for (let i = 0; i < maxLen; i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;
    if (num1 < num2) return -1;
    if (num1 > num2) return 1;
  }
  return 0;
}

/**
 * Mevcut sürümün zorunlu minimum sürümden eski olup olmadığını kontrol eder
 */
export function isVersionOutdated(clientVersion = CURRENT_CLIENT_VERSION, minRequiredVersion = '1.0.0') {
  return compareVersions(clientVersion, minRequiredVersion) < 0;
}

/**
 * Yerel sürüm yapılandırmasını alır
 */
export function getLocalVersionConfig() {
  try {
    const raw = localStorage.getItem(LOCAL_VERSION_KEY);
    return raw ? JSON.parse(raw) : {
      minVersion: '1.1.0',
      isForceUpdateActive: false,
      title: 'Önemli Sistem Güncellemesi',
      message: 'Uygulamamızın yeni sürümü yayınlandı. Kesintisiz kullanım ve anlık bildirimleri alabilmek için lütfen güncelleyiniz.',
      downloadUrl: '',
      updatedAt: new Date().toISOString()
    };
  } catch (e) {
    return {
      minVersion: '1.1.0',
      isForceUpdateActive: false,
      title: 'Önemli Sistem Güncellemesi',
      message: 'Uygulamamızın yeni sürümü yayınlandı.',
      downloadUrl: '',
      updatedAt: new Date().toISOString()
    };
  }
}

/**
 * Sürüm yapılandırmasını gerçek zamanlı dinler
 */
export function subscribeVersionConfig(db, isFirebaseActive, onUpdate) {
  if (isFirebaseActive && db) {
    try {
      const unsub = onSnapshot(doc(db, "notifications", "app_version_config"), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const config = {
            minVersion: data.minVersion || '1.1.0',
            isForceUpdateActive: !!data.isForceUpdateActive,
            title: data.title || 'Yeni Sürüm Güncellemesi',
            message: data.message || 'Sistemin kararlı çalışması için lütfen güncelleyiniz.',
            downloadUrl: data.downloadUrl || '',
            updatedAt: data.updatedAt || new Date().toISOString()
          };
          try {
            localStorage.setItem(LOCAL_VERSION_KEY, JSON.stringify(config));
          } catch (e) {}
          onUpdate(config);
        } else {
          onUpdate(getLocalVersionConfig());
        }
      }, (err) => {
        console.warn("Firestore sürüm yapılandırması dinleme uyarısı:", err);
        onUpdate(getLocalVersionConfig());
      });
      return unsub;
    } catch (e) {
      console.warn("Sürüm onSnapshot hatası:", e);
    }
  }

  onUpdate(getLocalVersionConfig());
  const handleStorage = (e) => {
    if (e.key === LOCAL_VERSION_KEY) {
      onUpdate(getLocalVersionConfig());
    }
  };
  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
}

/**
 * Süperadminin sürüm ayarını kaydeder
 */
export async function saveVersionConfig({
  minVersion,
  isForceUpdateActive = true,
  title = 'Önemli Sistem Güncellemesi',
  message = '',
  downloadUrl = '',
  db = null,
  isFirebaseActive = false
}) {
  const config = {
    isVersionConfig: true,
    minVersion: minVersion.trim() || '1.1.0',
    isForceUpdateActive: !!isForceUpdateActive,
    title: title.trim() || 'Yeni Sürüm Güncellemesi',
    message: message.trim() || 'Lütfen yeni sürüme güncelleyiniz.',
    downloadUrl: (downloadUrl || '').trim(),
    updatedAt: new Date().toISOString()
  };

  try {
    localStorage.setItem(LOCAL_VERSION_KEY, JSON.stringify(config));
  } catch (e) {}

  if (isFirebaseActive && db) {
    try {
      await setDoc(doc(db, "notifications", "app_version_config"), config, { merge: true });
      return config;
    } catch (err) {
      console.error("Firestore sürüm kaydetme hatası:", err);
    }
  }

  return config;
}
